/* global React, Icon, Card, Button, IconButton, Field, Input, Select, Currency, Switch, Tabs, Modal, EmptyState, Badge, toast, fmt, RecurringAgreementsList */
// PLUMME 3 - Avtaler: abonnement, repeterende fakturaer og lister.
const { useState: subUseState, useEffect: subUseEffect, useMemo: subUseMemo, useRef: subUseRef } = React;

const SUBSCRIPTION_COLORS = ['#EF4444', '#F08A3C', '#EABF3D', '#7CC84A', '#42BC79', '#2FA6A1', '#2A6FDB', '#5856D6', '#8E4EC6', '#C13FE0', '#E04393', '#6B7280'];
const subIntervals = () => [
  ['monthly', t('agreements.interval.monthly')],
  ['quarterly', t('agreements.interval.quarterly')],
  ['semiannual', t('agreements.interval.semiannual')],
  ['annual', t('agreements.interval.annual')],
];
const SUB_VAT = [25, 15, 12, 0];
const SUB_EMAIL_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
const SUB_EMAIL_ATTACHMENT_MAX_LABEL = '10 MB';
const emptyModel = () => ({ customers: [], groups: [], members: [], invoiceRuns: [], emailRuns: [], invoices: [], preservedSubscriptions: [], settings: {} });
const subId = (prefix) => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`);
const subToday = () => new Date().toISOString().slice(0, 10);
const subMoneyInputValue = (value) => window.PlummeMoneyInput.number(value, 0);
const subRoundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;
const subPercentValue = (value) => Math.max(0, Math.round(subMoneyInputValue(value) * 100) / 100);
const subPriceInclVat = (price, vatRate) => subRoundMoney(subMoneyInputValue(price) * (1 + (Number(vatRate) || 0) / 100));
const subPriceExVatFromIncl = (price, vatRate) => {
  const divisor = 1 + (Number(vatRate) || 0) / 100;
  return divisor ? subRoundMoney(subMoneyInputValue(price) / divisor) : subMoneyInputValue(price);
};
const subFmtDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
};
const subDate = (iso) => {
  const date = iso ? new Date(iso) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};
const subFullYearsBetween = (fromIso, toIso) => {
  const from = subDate(fromIso);
  const to = subDate(toIso) || new Date();
  if (!from || to <= from) return 0;
  let years = to.getFullYear() - from.getFullYear();
  if (to.getMonth() < from.getMonth() || (to.getMonth() === from.getMonth() && to.getDate() < from.getDate())) years -= 1;
  return Math.max(0, years);
};
const subSubscriptionAdjustedUnitPrice = (group, invoiceDate) => {
  const base = subMoneyInputValue(group?.unitPrice);
  if (!group?.cpiAdjustmentEnabled) return base;
  const years = subFullYearsBetween(group.startDate, invoiceDate || group.nextInvoiceDate || subToday());
  const rate = subPercentValue(group.cpiAdjustmentRate) / 100;
  return subRoundMoney(base * Math.pow(1 + rate, years));
};
const intervalLabel = (value) => (subIntervals().find(([key]) => key === value)?.[1] || t('agreements.interval.monthly'));
// 0 % er et gyldig valg (?? i stedet for ||), og perioden er antall maaneder per intervall.
const SUB_INTERVAL_MONTHS = { monthly: '1', quarterly: '3', semiannual: '6', annual: '12' };
const subInvoiceLineVatAndPeriod = (group) => { const vatRate = Number(group.vatRate ?? 25); return { vatRate, vat: vatRate === 0 ? 'exempt' : String(vatRate), vatTreatment: vatRate === 0 ? 'exempt' : 'standard', period: SUB_INTERVAL_MONTHS[group.interval] || '1' }; };
const customerName = (customer) => customer?.name || customer?.displayName || t('sub.unknownCustomer');
const customerEmail = (customer) => customer?.email && customer.email !== '-' ? customer.email : '';
const customerOrg = (customer) => customer?.org || customer?.organizationNumber || '';
const groupMemberRows = (model, groupId) => {
  const customersById = new Map(model.customers.map((customer) => [customer.id, customer]));
  return model.members
    .filter((member) => member.groupId === groupId && member.active !== false && !member.removedAt)
    .map((member) => ({ ...member, customer: customersById.get(member.customerId) }))
    .filter((member) => member.customer);
};
const subFmtPercent = (value) => String(subPercentValue(value)).replace('.', ',');
const groupMeta = (group, members) => {
  if (group.type !== 'subscription') return `${group.description ? `${group.description} · ` : ''}${t('sub.customersCount', '{count} customers', { count: members.length })}`;
  const cpi = group.cpiAdjustmentEnabled ? t('sub.cpiAnnualSuffix', ' · CPI {rate}% annually', { rate: subFmtPercent(group.cpiAdjustmentRate) }) : '';
  return `${fmt(group.unitPrice || 0)} · ${subFmtDate(group.startDate) || intervalLabel(group.interval)} · ${t('sub.customersCount', '{count} customers', { count: members.length })}${cpi}`;
};

// Het `SubColorPicker` og kolliderte med den i `products.jsx`, som bruker en annen
// fargeliste. Tekstene var like, sa feilen var usynlig — men den som ble lastet
// sist vant globalt, ogsa for den andre filas render.
function SubscriptionColorPicker({ value, onChange }) {
  return <div className="sub-color-picker">
    <div className="sub-color-row">
      {SUBSCRIPTION_COLORS.map((color) => <button key={color} type="button" className={'sub-color-dot' + (String(value).toLowerCase() === color.toLowerCase() ? ' active' : '')} aria-label={color} title={color}
        onClick={() => onChange(color)}
        style={{ background: color }} />)}
    </div>
    <label className="sub-color-spectrum" title={t('prd.pickOwnColour')}>
      <input type="color" value={value || SUBSCRIPTION_COLORS[0]} onChange={(event) => onChange(event.target.value)} />
    </label>
  </div>;
}

function SubActionMenu({ actions, label = t('sub.actionMenuLabel'), minWidth = 210 }) {
  const [open, setOpen] = subUseState(false);
  return <div className="popwrap sub-action-menu">
    <button type="button" className="btn btn-secondary btn-sm" onClick={(event) => { event.stopPropagation(); setOpen((current) => !current); }}><b>{label}</b><Icon name="chevDown" size={15} /></button>
    {open && <React.Fragment>
      <div className="pop-scrim" onClick={() => setOpen(false)} />
      {/* Side styres av CSS (.sub-action-menu), ikke inline — den må kunne snus
          når korthodet stables og knappen flytter seg til venstre. */}
      <div className="pop pop-menu" style={{ minWidth }}>
        {actions.filter(Boolean).map((action, index) => <button key={index} type="button" data-help={t('helpTopics.' + (action.helpTopic || 'groupMembership') + '.body')} data-help-title={action.label} onClick={(event) => { event.stopPropagation(); setOpen(false); action.onClick && action.onClick(); }} style={action.danger ? { color: 'var(--error)', fontWeight: 800 } : null}>
          {action.icon && <Icon name={action.icon} size={16} />}{action.label}
        </button>)}
      </div>
    </React.Fragment>}
  </div>;
}

function SubViewSelect({ value, onChange }) {
  const [open, setOpen] = subUseState(false);
  const options = [['subscription', t('agreements.tab.subscriptions')], ['recurring', t('agreements.tab.recurring')], ['list', t('agreements.tab.lists')]];
  const label = options.find(([key]) => key === value)?.[1] || t('agreements.tab.subscriptions');
  return <div className="popwrap sub-view-select">
    <button type="button" className="sub-view-trigger" onClick={() => setOpen((current) => !current)}
      data-help-title={t('vat.view')} data-help={t('sub.tabsHelp')}>
      {label}<Icon name="chevDown" size={15} />
    </button>
    {open && <React.Fragment>
      <div className="pop-scrim" onClick={() => setOpen(false)} />
      <div className="pop pop-menu" style={{ minWidth: 180, left: 0 }}>
        {options.map(([key, optionLabel]) => <button key={key} type="button" onClick={() => { setOpen(false); onChange(key); }}>
          {value === key && <Icon name="check" size={16} />}{optionLabel}
        </button>)}
      </div>
    </React.Fragment>}
  </div>;
}

function SubscriptionFormModal({ group, type, initial, onClose, onSave }) {
  const edit = !!group;
  const isSub = type === 'subscription';
  const [priceMode, setPriceMode] = subUseState('eks');
  const [form, setForm] = subUseState(() => ({
    type,
    name: initial?.name ?? group?.name ?? '',
    description: initial?.description ?? group?.description ?? '',
    internalComment: initial?.internalComment ?? group?.internalComment ?? '',
    unitPrice: initial?.price ?? group?.unitPrice ?? '',
    vatRate: initial?.vatRate ?? group?.vatRate ?? 25,
    interval: initial?.interval || group?.interval || 'monthly',
    dueDays: initial?.dueDays ?? group?.dueDays ?? 14,
    startDate: initial?.startDate || group?.startDate || subToday(),
    endDate: initial?.endDate ?? group?.endDate ?? '', nextInvoiceDate: initial?.nextInvoiceDate ?? group?.nextInvoiceDate ?? '',
    automaticSending: initial?.automaticSending ?? (group ? !!group.automaticSending : isSub),
    cpiAdjustmentEnabled: initial?.cpiAdjustmentEnabled ?? !!group?.cpiAdjustmentEnabled,
    cpiAdjustmentRate: initial?.cpiAdjustmentRate ?? group?.cpiAdjustmentRate ?? '',
    color: group?.color || (isSub ? SUBSCRIPTION_COLORS[0] : SUBSCRIPTION_COLORS[2]),
  }));
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const save = () => {
    if (!form.name.trim()) {
      toast(isSub ? t('sub.nameRequiredSubscription') : t('sub.nameRequiredList'), 'warning');
      return;
    }
    onSave({
      ...group,
      ...form,
      unitPrice: Number(form.unitPrice) || 0,
      vatRate: Number(form.vatRate) || 0,
      dueDays: Number.isFinite(Number(form.dueDays)) ? Math.max(0, Number(form.dueDays)) : 14,
      cpiAdjustmentEnabled: isSub ? !!form.cpiAdjustmentEnabled : false,
      cpiAdjustmentRate: isSub && form.cpiAdjustmentEnabled ? subPercentValue(form.cpiAdjustmentRate) : 0,
    });
  };
  return <Modal open onClose={onClose} title={edit ? (isSub ? t('sub.editSubTitle') : t('sub.editListTitle')) : (isSub ? t('sub.newSubTitle') : t('sub.newListTitle'))} max={760}
    footer={<div className="col gap-10" style={{ width: '100%' }}>
      <Button className="btn-block btn-lg" onClick={save}>{edit ? t('sub.saveChanges') : (isSub ? t('sub.saveSubscription') : t('sub.saveList'))}</Button>
      <Button variant="secondary" className="btn-block" onClick={onClose}>{t('common.cancel')}</Button>
    </div>}>
    <div className="grid-2 gap-14">
      <Field label={t('cust.form.contactName')}><Input value={form.name} onChange={(event) => set({ name: event.target.value })} placeholder={isSub ? t('sub.namePlaceholderSub') : t('sub.namePlaceholderList')} /></Field>
      <Field label={t('sale.lines.description')}><Input value={form.description} onChange={(event) => set({ description: event.target.value })} placeholder={t('clo.optionalDescription')} /></Field>
      {isSub && <React.Fragment>
        <Field label={<button type="button" className="amount-mode-label" onClick={() => setPriceMode((mode) => mode === 'eks' ? 'inkl' : 'eks')} title={t('sale.lines.toggleVat')} data-help={t('helpTopics.unitPrice.body')} data-help-title={t('helpTopics.unitPrice.title')}>{t('sharedText.prd.price')} {priceMode === 'eks' ? 'eks.' : 'inkl.'} {t('sharedText.vat.vat')}<Icon name="refresh" size={12} /></button>}>
          {priceMode === 'eks'
            ? <Currency value={form.unitPrice} onChange={(event) => set({ unitPrice: event.target.value })} placeholder="0" />
            : <Currency value={subPriceInclVat(form.unitPrice, form.vatRate)} onChange={(event) => set({ unitPrice: subPriceExVatFromIncl(event.target.value, form.vatRate) })} placeholder="0" />}
        </Field>
        <Field label={t('ui.summary.vat')} help={t('helpTopics.vatTreatment.body')} helpTitle={t('helpTopics.vatTreatment.title')}><Select value={form.vatRate} onChange={(event) => set({ vatRate: Number(event.target.value) })}>{SUB_VAT.map((rate) => <option key={rate} value={rate}>{rate} %</option>)}</Select></Field>
        <Field label={t('ui.period')} help={t('helpTopics.recurring.body')} helpTitle={t('helpTopics.recurring.title')}><Select value={form.interval} onChange={(event) => set({ interval: event.target.value })}>{subIntervals().map(([key, label]) => <option key={key} value={key}>{label}</option>)}</Select></Field>
        <Field label={t('cust.ledger.due')} help={t('helpTopics.recurring.body')} helpTitle={t('helpTopics.recurring.title')}><Input inputMode="numeric" value={form.dueDays} onChange={(event) => set({ dueDays: event.target.value })} /></Field>
        <Field label={t('sub.start')} help={t('helpTopics.recurring.body')} helpTitle={t('helpTopics.recurring.title')}><Input type="date" value={form.startDate} onChange={(event) => set({ startDate: event.target.value })} /></Field>
        <Field label={t('sub.end')} help={t('helpTopics.recurring.body')} helpTitle={t('helpTopics.recurring.title')}><Input type="date" value={form.endDate} onChange={(event) => set({ endDate: event.target.value })} /><div className="t-xs muted" style={{ marginTop: 6 }}>{t('sub.endHelp')}</div></Field><Field label={t('sub.nextInvoiceDateLabel')} help={t('helpTopics.recurring.body')} helpTitle={t('helpTopics.recurring.title')}><Input type="date" value={form.nextInvoiceDate} onChange={(event) => set({ nextInvoiceDate: event.target.value })} /></Field>
        <div className="sub-cpi-card">
          <div className="row between gap-12">
            <div><div className="sub-cpi-title">{t('agreements.autoSend')}</div><div className="t-xs muted">{t('agreements.autoSendHelp')}</div></div>
            <Switch on={!!form.automaticSending} onChange={(on) => set({ automaticSending: on })} />
          </div>
        </div>
        <div className="sub-cpi-card">
          <div className="row between gap-12">
            <div>
              <div className="sub-cpi-title">{t('sub.cpiAdjustmentLabel')}</div>
              <div className="t-xs muted">{t('sub.cpiAdjustmentHelp')}</div>
            </div>
            <Switch on={!!form.cpiAdjustmentEnabled} onChange={(on) => set({ cpiAdjustmentEnabled: on, cpiAdjustmentRate: on ? form.cpiAdjustmentRate : '' })} />
          </div>
          {form.cpiAdjustmentEnabled && <Field label={t('sharedText.vat.rate')} className="mt-12">
            <div className="input-affix"><Input className="input" inputMode="decimal" value={form.cpiAdjustmentRate} onChange={(event) => set({ cpiAdjustmentRate: event.target.value })} placeholder="0,0" /><span className="affix">%</span></div>
          </Field>}
        </div>
      </React.Fragment>}
    </div>
    <Field label={t('sub.internalComment')} className="mt-16"><textarea className="input" rows={3} value={form.internalComment} onChange={(event) => set({ internalComment: event.target.value })} /></Field>
    <Field label={t('po.colour')} className="mt-16"><SubscriptionColorPicker value={form.color} onChange={(color) => set({ color })} /></Field>
  </Modal>;
}

function SubscriptionCustomerModal({ name, onClose, onSave }) {
  const [form, setForm] = subUseState({ name: name || '', org: '', email: '', phone: '', addr: '', zip: '', city: '', followUp: '', note: '' });
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  return <Modal open onClose={onClose} title={t('sale.cust.new')} max={620}
    footer={<React.Fragment><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button><Button icon="check" onClick={() => onSave(form)}>{t('cust.form.save')}</Button></React.Fragment>}>
    <div className="col gap-14" style={{ paddingBottom: 4 }}>
      <div className="grid-2 gap-14">
        <Field label={t('cust.merge.name')}><Input value={form.name} onChange={(event) => set({ name: event.target.value })} placeholder={t('cust.form.namePh')} /></Field>
        <Field label={t('cust.form.org')}><Input value={form.org} onChange={(event) => set({ org: event.target.value })} placeholder={t('sale.cust.orgPh')} /></Field>
        <Field label={t('cust.col.email')}><Input value={form.email} onChange={(event) => set({ email: event.target.value })} placeholder={t('sale.cust.emailPh')} /></Field>
        <Field label={t('cust.col.phone')}><Input value={form.phone} onChange={(event) => set({ phone: event.target.value })} placeholder={t('sale.cust.phonePh')} /></Field>
        <Field label={t('cust.form.address')}><Input value={form.addr} onChange={(event) => set({ addr: event.target.value })} placeholder={t('cust.form.addressPh')} /></Field>
        <Field label={t('cust.form.zip')}><Input value={form.zip} onChange={(event) => set({ zip: event.target.value })} placeholder="0000" /></Field>
        <Field label={t('cust.form.city')}><Input value={form.city} onChange={(event) => set({ city: event.target.value })} placeholder={t('cust.form.cityPh')} /></Field>
        <Field label={t('cust.nextFollowUp')}><Input type="date" value={form.followUp} onChange={(event) => set({ followUp: event.target.value })} /></Field>
      </div>
      <Field label={t('cust.form.note')}><textarea className="input" rows={3} value={form.note} onChange={(event) => set({ note: event.target.value })} placeholder={t('cust.form.notePh')} /></Field>
    </div>
  </Modal>;
}

function AddCustomersModal({ group, model, onClose, onSave }) {
  const existingIds = groupMemberRows(model, group.id).map((member) => member.customerId);
  const [query, setQuery] = subUseState('');
  const [selected, setSelected] = subUseState(() => new Set(existingIds));
  const [page, setPage] = subUseState(1);
  const [busy, setBusy] = subUseState(false);
  const [newCustomerName, setNewCustomerName] = subUseState('');
  const per = 10;
  const normalized = query.trim().toLowerCase();
  const customers = model.customers.filter((customer) => {
    if (!normalized) return true;
    return `${customerName(customer)} ${customerEmail(customer)} ${customerOrg(customer)}`.toLowerCase().includes(normalized);
  });
  const exact = query.trim() && model.customers.some((customer) => customerName(customer).trim().toLowerCase() === normalized);
  const pages = Math.max(1, Math.ceil(customers.length / per));
  const cur = Math.min(page, pages);
  const shown = customers.slice((cur - 1) * per, cur * per);
  const toggle = (id) => setSelected((current) => { const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const saveNewCustomer = async (customer) => {
    const name = customer.name?.trim();
    if (!name || exact || !window.PlummeBackend?.saveCustomerRecord) return;
    setBusy(true);
    try {
      const saved = await window.PlummeBackend.saveCustomerRecord({ id: subId('customer'), ...customer, name, status: 'active' });
      if (saved?.id) setSelected((current) => new Set([...current, saved.id]));
      setNewCustomerName('');
      setQuery('');
      setPage(1);
      toast(t('sub.customerSavedToast', 'Customer "{name}" saved', { name }), 'success');
    } catch (error) {
      toast(error?.message || t('sub.saveCustomerError'), 'error');
    } finally {
      setBusy(false);
    }
  };
  const save = () => onSave(Array.from(selected));
  return <Modal open onClose={onClose} title={t('sub.addCustomersTitle', 'Add customers · {name}', { name: group.name })} max={720}
    footer={<div className="row between" style={{ width: '100%' }}><Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button><Button onClick={save} disabled={busy}>{t('sub.saveCustomers')}</Button></div>}>
    <Field label={t('sub.searchCustomers')}><Input value={query} onChange={(event) => { setQuery(event.target.value); setPage(1); }} placeholder={t('sub.searchCustomersPh')} /></Field>
    <div className="row gap-8 wrap" style={{ justifyContent: 'flex-end', margin: '12px 0' }}>
      <Button size="sm" variant="secondary" onClick={() => setSelected((current) => new Set([...current, ...shown.map((customer) => customer.id)]))}>{t('sub.selectVisible')}</Button>
      <Button size="sm" variant="secondary" onClick={() => setSelected((current) => { const next = new Set(current); shown.forEach((customer) => next.delete(customer.id)); return next; })}>{t('sub.deselectVisible')}</Button>
      <span className="t-sm muted" style={{ fontWeight: 700, alignSelf: 'center' }}>{t('sub.selectedCount', '{count} selected', { count: selected.size })}</span>
    </div>
    <div className="col gap-8" style={{ maxHeight: 340, overflow: 'auto' }}>
      {query.trim() && !exact && <button type="button" className="row between" disabled={busy} style={{ padding: '12px 14px', border: '1px dashed var(--plum-300)', borderRadius: 'var(--r-ms)', background: 'var(--plum-50)', textAlign: 'left' }} onClick={() => setNewCustomerName(query.trim())}>
        <span className="row gap-10" style={{ color: 'var(--plum-700)', fontWeight: 800 }}><span className="icon-chip" style={{ width: 28, height: 28, borderRadius: 'var(--r-sm)' }}><Icon name="plus" size={16} /></span>{t('sub.addAsNewCustomer', 'Add "{name}" as a new customer', { name: query.trim() })}</span><Icon name="chevRight" size={16} className="muted" />
      </button>}
      {shown.map((customer) => <label key={customer.id} className={'sub-pick' + (selected.has(customer.id) ? ' on' : '')}>
        <input type="checkbox" checked={selected.has(customer.id)} onChange={() => toggle(customer.id)} />
        <span className="col" style={{ gap: 1 }}><b>{customerName(customer)}</b><span className="t-sm muted">{customerEmail(customer) || t('sub.noContactInfo')}</span></span>
      </label>)}
      {shown.length === 0 && !query.trim() && <EmptyState compact noVisual title={t('sub.noCustomers')} text={t('sub.addCustomersEmptyText')} />}
    </div>
    <div className="row between" style={{ marginTop: 14, alignItems: 'center' }}>
      <span className="t-sm muted">{customers.length === 0 ? '0' : `${(cur - 1) * per + 1}-${Math.min(cur * per, customers.length)}`} / {customers.length}</span>
      <div className="row gap-6">
        <IconButton name="chevLeft" size="sm" variant="ghost" onClick={() => setPage(Math.max(1, cur - 1))} aria-label={t('cust.prev')} />
        <span className="t-sm" style={{ fontWeight: 800, alignSelf: 'center' }}>{cur} / {pages}</span>
        <IconButton name="chevRight" size="sm" variant="ghost" onClick={() => setPage(Math.min(pages, cur + 1))} aria-label={t('cust.next')} />
      </div>
    </div>
    {newCustomerName && <SubscriptionCustomerModal name={newCustomerName} onClose={() => setNewCustomerName('')} onSave={saveNewCustomer} />}
  </Modal>;
}

function SendChoiceModal({ group, onClose, onPostplumma, onDraft }) {
  return <Modal open onClose={onClose} title={t('sub.sendInvoiceTitle', 'Send invoice · {name}', { name: group.name })} max={560}>
    <p className="muted" style={{ fontWeight: 600, marginBottom: 16 }}>{t('sub.howToInvoice')}</p>
    <p className="t-sm muted" style={{ fontWeight: 650, marginTop: -8, marginBottom: 16 }}>{t('sub.manualFlowNote')}</p>
    <div className="col gap-12" style={{ paddingBottom: 12 }}>
      <button type="button" className="card card-pad row between" style={{ cursor: 'pointer', textAlign: 'left' }} onClick={onPostplumma}>
        <span className="row gap-12" style={{ minWidth: 0 }}><span className="icon-chip"><Icon name="send" size={18} /></span><span className="col" style={{ gap: 2 }}><b>{t('sub.sendInvoice')}</b><span className="t-sm muted">{t('sub.openDraftFilled')}</span></span></span>
        <Icon name="chevRight" size={18} className="muted" />
      </button>
      <button type="button" className="card card-pad row between" style={{ cursor: 'pointer', textAlign: 'left' }} onClick={onDraft}>
        <span className="row gap-12" style={{ minWidth: 0 }}><span className="icon-chip"><Icon name="fileText" size={18} /></span><span className="col" style={{ gap: 2 }}><b>{t('cust.act.newInvoice')}</b><span className="t-sm muted">{t('sub.openBlankDraft')}</span></span></span>
        <Icon name="chevRight" size={18} className="muted" />
      </button>
    </div>
  </Modal>;
}

function EmailModal({ group, members, onClose, onSend }) {
  const [subject, setSubject] = subUseState('');
  const [message, setMessage] = subUseState('');
  const [attachments, setAttachments] = subUseState([]);
  const [dropActive, setDropActive] = subUseState(false);
  const [busy, setBusy] = subUseState(false);
  const fileRef = subUseRef(null);
  // Foretaksdokumentene legges paa av backend ved sending. Her vises de bare, saa
  // avsenderen vet hva kunden faar uten aa maatte aapne innstillingene.
  const standardDocuments = subUseMemo(() => window.PlummeCompanyDocuments?.selectForTarget(
    window.PlummeBackend?.getCompanyDocuments?.() || [],
    'subscription',
  ) || [], []);
  const ready = members.filter((member) => customerEmail(member.customer)).length;
  const missing = members.length - ready;
  const attachmentFiles = attachments.map((attachment) => attachment.file).filter(Boolean);
  const addFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((file) => file instanceof File);
    if (!files.length || busy) return;
    const oversized = files.filter((file) => Number(file.size || 0) > SUB_EMAIL_ATTACHMENT_MAX_BYTES);
    if (oversized.length) {
      toast(t('sub.attachmentTooLarge', 'Attachments can be max {max}.', { max: SUB_EMAIL_ATTACHMENT_MAX_LABEL }), 'error');
      return;
    }
    setAttachments((current) => [
      ...current,
      ...files.map((file) => ({
        id: subId('email-attachment'),
        file,
        name: file.name || `utklipp-${Date.now()}`,
        size: file.size || 0,
      })),
    ]);
  };
  const clearFileInput = () => { if (fileRef.current) fileRef.current.value = ''; };
  const removeAttachment = (id) => {
    setAttachments((current) => current.filter((attachment) => attachment.id !== id));
    clearFileInput();
  };
  subUseEffect(() => {
    const hasFiles = (event) => event.dataTransfer && Array.from(event.dataTransfer.types || []).includes('Files');
    const onDragOver = (event) => {
      if (!hasFiles(event) || busy) return;
      event.preventDefault();
      setDropActive(true);
    };
    const onDragLeave = (event) => {
      if (!event.relatedTarget) setDropActive(false);
    };
    const onDrop = (event) => {
      if (!hasFiles(event) || busy) return;
      event.preventDefault();
      setDropActive(false);
      addFiles(event.dataTransfer.files);
    };
    const onPaste = (event) => {
      if (busy) return;
      const files = [];
      files.push(...Array.from(event.clipboardData?.files || []));
      Array.from(event.clipboardData?.items || []).forEach((item) => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      });
      if (!files.length) return;
      event.preventDefault();
      addFiles(files);
    };
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('dragleave', onDragLeave);
    document.addEventListener('drop', onDrop);
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('drop', onDrop);
      document.removeEventListener('paste', onPaste);
    };
  }, [busy]);
  const send = async () => {
    if (!subject.trim()) {
      toast(t('sub.fillSubject'), 'warning');
      return;
    }
    if (!message.trim()) {
      toast(t('sub.writeMessage'), 'warning');
      return;
    }
    setBusy(true);
    try {
      await onSend({ subject, message, attachmentFiles, count: ready });
    } finally {
      setBusy(false);
    }
  };
  return <Modal open onClose={onClose} title={t('sub.sendEmailTitle', 'Send email · {name}', { name: group.name })} max={760}
    footer={<div className="row between" style={{ width: '100%' }}><Button variant="secondary" onClick={onClose} disabled={busy}>{t('common.cancel')}</Button><Button icon="send" onClick={send} disabled={busy || ready === 0}>{busy ? t('sub.sending') : t('sub.sendEmailButton')}</Button></div>}>
    <div className={'sub-email-drop-scope' + (dropActive ? ' is-dragging' : '')}>
    <div className="row gap-8" style={{ marginBottom: 14 }}><span className="sub-pill">{t('sub.readyCount', '{count} ready', { count: ready })}</span><span className="sub-pill">{missing} {t('sharedText.inv.bulk.noEmail')}</span></div>
    <Field label={t('sub.subject')}><Input value={subject} onChange={(event) => setSubject(event.target.value)} /></Field>
    <Field label={t('sub.message')} className="mt-12"><textarea className="input" rows={6} value={message} onChange={(event) => setMessage(event.target.value)} /></Field>
    <div className="t-sm" style={{ fontWeight: 800, margin: '16px 0 7px' }}>{t('sale.att.title')}</div>
    <input ref={fileRef} type="file" multiple style={{ display: 'none' }} accept="application/pdf,image/png,image/jpeg,image/webp,.doc,.docx,.xls,.xlsx" onChange={(event) => { addFiles(event.target.files); clearFileInput(); }} />
    <button type="button" className="sub-drop" onClick={() => fileRef.current?.click()} disabled={busy}><Icon name="upload" size={18} />{attachments.length ? t('sub.attachmentsAddedCount', '{count} attachments added', { count: attachments.length }) : t('sub.addAttachment')}</button>
    {attachments.length > 0 && <div className="sub-attachment-list">{attachments.map((attachment) => <div key={attachment.id} className="sub-attachment-row"><span className="row gap-8" style={{ minWidth: 0 }}><Icon name="fileText" size={15} /><span>{attachment.name}</span></span><button type="button" onClick={() => removeAttachment(attachment.id)} disabled={busy} aria-label={t('asset.removeAttachment')}><Icon name="trash" size={14} /></button></div>)}</div>}
    {attachments.length > 0 && <button type="button" className="btn btn-ghost btn-sm mt-8" onClick={() => { setAttachments([]); clearFileInput(); }} disabled={busy}>{t('sub.removeAllAttachments')}</button>}
    {standardDocuments.length > 0 && <p className="t-xs muted" style={{ marginTop: 10 }}>{t('sub.standardDocs', { names: standardDocuments.map((document) => document.name).join(', ') })}</p>}
    <p className="t-xs muted" style={{ marginTop: 10 }}>{t('sub.emailSendNote')}</p>
    </div>
  </Modal>;
}

function SubscriptionCard({ group, model, expanded, onToggle, onAdd, onEdit, onSendInvoice, onEmail, onPause, onArchive, onSendMemberInvoice, onRemoveMember }) {
  const members = groupMemberRows(model, group.id);
  const invoices = model.invoiceRuns.filter((run) => run.groupId === group.id);
  const emails = model.emailRuns.filter((run) => run.groupId === group.id);
  const [tab, setTab] = subUseState('kunder');
  const isSub = group.type === 'subscription';
  const color = group.color || SUBSCRIPTION_COLORS[0];
  return <div className="sub-card" style={{ borderLeft: `5px solid ${color}`, background: `color-mix(in srgb, ${color} 8%, var(--card))` }}>
    <div className="sub-card-hd">
      <div className="col" style={{ gap: 2, minWidth: 0 }}>
        <b className="t-h3" style={{ fontSize: 17 }}>{group.name}{group.status === 'pause' && <span className="sub-pausetag">{t('cust.crm.pause')}</span>}</b>
        <span className="t-sm muted">{isSub ? t('sub.typeSubscription') : t('sub.typeList')}</span>
        <span className="t-sm muted">{groupMeta(group, members)}</span>{group.lastError && <span className="t-sm" style={{ color: 'var(--error)' }}>{window.recurringErrorText?.(group)}</span>}
      </div>
      <SubActionMenu actions={[
        { label: expanded ? t('sub.actionClose') : t('sub.actionOpen'), icon: expanded ? 'chevUp' : 'eye', onClick: onToggle },
        { label: t('sub.addCustomers'), icon: 'plus', onClick: onAdd },
        { label: t('sub.edit'), icon: 'penBox', onClick: onEdit },
        group.status !== 'pause' ? { label: t('sub.sendInvoice'), icon: 'send', helpTopic: 'invoiceIssue', onClick: onSendInvoice } : null,
        { label: t('sub.emailLabel'), icon: 'mail', helpTopic: 'chatVisibility', onClick: onEmail },
        isSub ? { label: group.status === 'pause' ? t('sub.resume') : t('sub.pause'), icon: 'clock', helpTopic: 'recurringAction', onClick: onPause } : null,
        { label: t('sub.delete'), icon: 'trash', danger: true, helpTopic: 'recurringAction', onClick: onArchive },
      ]} />
    </div>
    {expanded && <div className="sub-detail">
      <Tabs value={tab} onChange={setTab} options={[
        { value: 'kunder', label: t('sub.tabCustomers', 'Customers ({count})', { count: members.length }) },
        { value: 'faktura', label: t('sub.tabInvoices', 'Invoices ({count})', { count: invoices.length }) },
        { value: 'epost', label: t('sub.tabEmails', 'Emails ({count})', { count: emails.length }) },
      ]} />
      {tab === 'kunder' && <div className="col gap-10 mt-14">
        {members.length === 0 ? <EmptyState compact noVisual title={t('sub.noCustomers')} text={t('sub.addCustomersToInvoiceText')} action={<Button size="sm" icon="plus" onClick={onAdd}>{t('sub.addCustomers')}</Button>} /> :
          members.map((member) => <div key={member.id} className="sub-cust"><div className="col" style={{ gap: 1 }}><b>{customerName(member.customer)}</b><span className="t-sm muted">{t('common.active')}</span>{group.automaticSending && !window.recurringHasSendableEmail?.(member.customer) && <window.RecurringMissingEmailWarning compact />}</div><SubActionMenu minWidth={180} actions={[
            { label: t('sub.sendInvoice'), icon: 'send', onClick: () => onSendMemberInvoice(member) },
            { label: t('sub.removeMember'), icon: 'trash', danger: true, onClick: () => onRemoveMember(member) },
          ]} /></div>)}
      </div>}
      {tab === 'faktura' && <div className="mt-14">{invoices.length === 0 ? <EmptyState compact noVisual title={t('sub.noInvoiceSent')} text={t('sub.noInvoiceSentText')} /> : invoices.map((run) => <div key={run.id} className="sub-cust"><b>{run.createdAt?.slice(0, 10) || t('sub.invoiceRunFallback')}</b><span className="t-sm muted">{run.status === 'blocked' ? `${t('agreements.notSent')}: ${window.recurringErrorText?.(run) || ''}` : run.status || t('sub.statusSaved')}</span></div>)}</div>}
      {tab === 'epost' && <div className="mt-14">{emails.length === 0 ? <EmptyState compact noVisual title={t('sub.noEmailSent')} text={t('sub.noEmailSentText')} /> : emails.map((run) => <div key={run.id} className="sub-cust"><div><b>{run.subject || t('sub.emailLogFallback')}</b><div className="t-sm muted">{run.createdAt?.slice(0, 10)} · {t('sub.recipientsCount', '{count} recipients', { count: run.count || 0 })}</div></div></div>)}</div>}
    </div>}
  </div>;
}

function AbonnementPage({ setRoute }) {
  const [model, setModel] = subUseState(emptyModel);
  const [view, setView] = subUseState('subscription');
  const [search, setSearch] = subUseState('');
  const [expanded, setExpanded] = subUseState(null);
  const [modal, setModal] = subUseState(null);
  const [busy, setBusy] = subUseState(false);
  const refresh = () => setModel(window.PlummeBackend?.getCustomerBillingModel?.() || emptyModel());
  subUseEffect(() => {
    refresh();
    window.addEventListener('plumme-backend-state', refresh);
    window.addEventListener('plumme-customers-changed', refresh);
    return () => {
      window.removeEventListener('plumme-backend-state', refresh);
      window.removeEventListener('plumme-customers-changed', refresh);
    };
  }, []);
  const groups = subUseMemo(() => {
    return model.groups
      .filter((group) => !group.archivedAt && group.active !== false && group.type === view)
      .filter((group) => {
        const members = groupMemberRows(model, group.id).map((member) => `${customerName(member.customer)} ${customerEmail(member.customer)}`).join(' ');
        return window.PlummeListSearch.matches(search, {
          text: [group.name, group.description, group.internalComment, members],
          amounts: [group.unitPrice],
        });
      });
  }, [model, search, view]);
  const saveGroup = async (group) => {
    setBusy(true);
    try {
      const saved = await window.PlummeBackend.saveCustomerBillingGroup(group);
      setExpanded(saved.id);
      setModal(null);
      refresh();
      toast(group.id ? t('sub.changesSaved') : (group.type === 'list' ? t('sub.listCreated') : t('sub.subscriptionCreated')), 'success');return saved;
    } catch (error) {
      toast(error?.message || t('sub.saveError'), 'error');return null;
    } finally {
      setBusy(false);
    }
  };
  const saveMembers = async (groupId, customerIds) => {
    setBusy(true);
    try {
      await window.PlummeBackend.saveCustomerBillingGroupMembers(groupId, customerIds);
      setModal(null);
      refresh();
      toast(t('sub.customersSaved'), 'success');return true;
    } catch (error) {
      toast(error?.message || t('sub.saveCustomersError'), 'error');return false;
    } finally {
      setBusy(false);
    }
  };
  const setGroupStatus = async (group, status) => {
    setBusy(true);
    try {
      await window.PlummeBackend.setCustomerBillingGroupStatus(group.id, status);
      refresh();
      toast(status === 'active' ? t('sub.agreementResumed') : t('sub.agreementPaused'), 'success');return true;
    } catch (error) {
      toast(error?.message || t('sub.updateAgreementError'), 'error');return false;
    } finally {
      setBusy(false);
    }
  };const archiveGroup=async(group)=>{if(busy)return null;setBusy(true);try{const saved=await window.PlummeBackend.archiveCustomerBillingGroup(group.id);refresh();toast(group.type==='list'?t('sub.listDeleted'):t('sub.subscriptionDeleted'),'success');return saved||group;}catch(error){toast(error?.message||t('sub.deleteError'),'error');return null;}finally{setBusy(false);}};
  const openDraft = (group, options = {}) => {
    const members = groupMemberRows(model, group.id);
    const withSubscriptionLine = options.withSubscriptionLine !== false && group.type === 'subscription';
    const invoiceDate = group.nextInvoiceDate || subToday();
    window.PlummeBackend?.setOpenInvoice?.({ type: 'faktura', customer: group.name, customerIds: members.map((member) => member.customerId), lines: withSubscriptionLine ? [{ desc: group.name, qty: 1, price: subSubscriptionAdjustedUnitPrice(group, invoiceDate), ...subInvoiceLineVatAndPeriod(group), type: 'Abonnement', subStart: group.startDate || subToday(), subEnd: group.endDate || '' }] : [] });
    setRoute && setRoute('faktura');
  };
  const openMemberDraft = (group, member) => {
    const invoiceDate = member.nextInvoiceDate || group.nextInvoiceDate || subToday();
    window.PlummeBackend?.setOpenInvoice?.({ type: 'faktura', customer: customerName(member.customer),
      lines: group.type === 'subscription' ? [{ desc: group.name, qty: 1, price: subSubscriptionAdjustedUnitPrice(group, invoiceDate), ...subInvoiceLineVatAndPeriod(group), type: 'Abonnement', subStart: group.startDate || subToday(), subEnd: group.endDate || '' }] : [] });
    setRoute && setRoute('faktura');
  };
  const sendEmailRun = async (group, payload) => {
    setBusy(true);
    try {
      const members = groupMemberRows(model, group.id);
      const result = await window.PlummeBackend.sendCustomerBillingGroupEmail({
        groupId: group.id,
        subject: payload.subject,
        message: payload.message,
        // Modalen leverer flere filer. Feltnavnet var i entall her, og alle
        // vedlegg brukeren la ved ble stille borte for e-posten ble sendt.
        attachmentFiles: payload.attachmentFiles,
        recipients: members.map((member) => ({
          memberId: member.id,
          customerId: member.customerId,
          customerName: customerName(member.customer),
          email: customerEmail(member.customer),
        })),
      });
      setModal(null);
      refresh();
      toast(`${t('sub.emailsSentToast', '{count} emails sent', { count: result.sentCount || 0 })}${result.failedCount ? t('sub.emailsSentFailedSuffix', ', {count} not sent', { count: result.failedCount }) : ''}.`, 'success');
    } catch (error) {
      toast(error?.message || t('sub.sendEmailError'), 'error');
    } finally {
      setBusy(false);
    }
  };
  const isSub = view === 'subscription';
  const isRecurring = view === 'recurring';useSubscriptionPageControlAdapter({view,setView,modal,setModal,groups:model.groups||[],customers:model.customers||[],members:model.members||[],saving:busy,save:saveGroup,saveMembers,setStatus:setGroupStatus,archive:archiveGroup});
  return <Card pad={false} style={{ padding: '22px 24px' }}>
    <div className="row between wrap gap-12" style={{ marginBottom: 16 }}>
      <SubViewSelect value={view} onChange={(next) => { setView(next); setExpanded(null); }} />
      <Button icon="plus" onClick={() => isRecurring ? setRoute?.('faktura') : setModal({ type: isSub ? 'newSub' : 'newList' })} title={isRecurring ? t('agreements.newRecurringTitle') : isSub ? t('sub.newSubTitle') : t('sub.newListTitle')}><span className="sub-add-label">{isRecurring ? t('agreements.newRecurring') : isSub ? t('sub.newSubTitle') : t('sub.newListTitle')}</span></Button>
    </div>
    <Field label={t('list.search')}><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={isRecurring ? t('agreements.searchRecurring') : isSub ? t('sub.searchSubPlaceholder') : t('sub.searchListPlaceholder')} /></Field>
    <div className="col gap-14 mt-16">
      {isRecurring ? <RecurringAgreementsList model={model} search={search} onStatusChange={setGroupStatus} /> : groups.length === 0 ? <EmptyState noVisual title={isSub ? t('sub.emptySubTitle') : t('sub.emptyListTitle')} text={isSub ? t('sub.emptySubText') : t('sub.emptyListText')} action={<Button icon="plus" onClick={() => setModal({ type: isSub ? 'newSub' : 'newList' })}>{isSub ? t('sub.newSubTitle') : t('sub.newListTitle')}</Button>} /> :
        groups.map((group) => <SubscriptionCard key={group.id} group={group} model={model} expanded={expanded === group.id}
          onToggle={() => setExpanded((current) => current === group.id ? null : group.id)}
          onAdd={() => setModal({ type: 'addCustomers', group })}
          onEdit={() => setModal({ type: 'edit', group })}
          onSendInvoice={() => group.type === 'subscription' ? setModal({ type: 'sendChoice', group }) : openDraft(group)}
          onEmail={() => setModal({ type: 'email', group })}
          onPause={() => setGroupStatus(group, group.status === 'pause' ? 'active' : 'pause')}
          onSendMemberInvoice={(member) => openMemberDraft(group, member)}
          onRemoveMember={(member) => saveMembers(group.id, groupMemberRows(model, group.id).filter((entry) => entry.customerId !== member.customerId).map((entry) => entry.customerId))}
          onArchive={() => archiveGroup(group)} />)}
    </div>
    {model.preservedSubscriptions.length > 0 && <div className="alert alert-info mt-16"><Icon name="info" size={18} /><span>{t('sub.preservedSubscriptionsNotice', '{count} older customer subscriptions are preserved in app state and can be migrated later.', { count: model.preservedSubscriptions.length })}</span></div>}
    {modal?.type === 'newSub' && <SubscriptionFormModal key={JSON.stringify(modal.assistantFields||{})} type="subscription" initial={modal.assistantFields} onClose={() => setModal(null)} onSave={saveGroup} />}
    {modal?.type === 'newList' && <SubscriptionFormModal type="list" onClose={() => setModal(null)} onSave={saveGroup} />}
    {modal?.type === 'edit' && <SubscriptionFormModal group={modal.group} type={modal.group.type} initial={modal.assistantFields} onClose={() => setModal(null)} onSave={saveGroup} />}
    {modal?.type === 'addCustomers' && <AddCustomersModal group={modal.group} model={model} onClose={() => setModal(null)} onSave={(ids) => saveMembers(modal.group.id, ids)} />}
    {modal?.type === 'sendChoice' && <SendChoiceModal group={modal.group} onClose={() => setModal(null)} onPostplumma={() => { const group = modal.group; setModal(null); openDraft(group, { withSubscriptionLine: true }); }} onDraft={() => { const group = modal.group; setModal(null); openDraft(group, { withSubscriptionLine: false }); }} />}
    {modal?.type === 'email' && <EmailModal group={modal.group} members={groupMemberRows(model, modal.group.id)} onClose={() => setModal(null)} onSend={(payload) => sendEmailRun(modal.group, payload)} />}
  </Card>;
}

Object.assign(window, { AbonnementPage });
